import type { AccountRow, SummaryResponse, TransactionRow } from './types';

/**
 * Same-origin API in all environments:
 *   - Production: Worker Route finance.utproducts.com/api/* -> utp-financials
 *     (Pages serves the dashboard HTML/assets; Worker handles /api/*)
 *   - Dev: vite proxies /api -> http://localhost:8787 (see vite.config.ts)
 *
 * Same-origin avoids CORS + Cloudflare Access cross-domain cookie headaches.
 */
const BASE = '';

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (res.status === 401) {
    // Cloudflare Access redirects unauthenticated requests to its login page,
    // so in practice we almost never see this. But if auth is the simple
    // password mode, 401 means the cookie expired — send the user to /login.
    throw new ApiError('Unauthorized', 401);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    // Try to surface a server-provided `detail` field if present
    let detail = body;
    try {
      const parsed = JSON.parse(body);
      if (parsed?.detail) detail = String(parsed.detail);
      else if (parsed?.error) detail = String(parsed.error);
    } catch { /* body was not JSON */ }
    throw new ApiError(detail || `HTTP ${res.status}`, res.status);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;
  constructor(msg: string, status: number) { super(msg); this.status = status; }
}

export const api = {
  summary(date: string): Promise<SummaryResponse> {
    return req(`/api/summary?date=${encodeURIComponent(date)}`);
  },
  transactions(params: { from?: string; to?: string; account_id?: string; category_slug?: string; limit?: number }) {
    const q = new URLSearchParams();
    if (params.from)          q.set('from', params.from);
    if (params.to)            q.set('to', params.to);
    if (params.account_id)    q.set('account_id', params.account_id);
    if (params.category_slug) q.set('category_slug', params.category_slug);
    if (params.limit)         q.set('limit', String(params.limit));
    return req<{ transactions: TransactionRow[] }>(`/api/transactions?${q}`);
  },
  flagged(): Promise<{ flagged: TransactionRow[] }> {
    return req('/api/flagged');
  },
  accounts(): Promise<{ results: AccountRow[] }> {
    return req('/api/accounts');
  },
  sync(): Promise<{ started_at: string; finished_at: string; processed: number }> {
    return req('/api/sync', { method: 'POST' });
  },
  ack(transaction_id: string) {
    return req('/api/ack', { method: 'POST', body: JSON.stringify({ transaction_id }) });
  },
  categorize(transaction_id: string, category_slug: string) {
    return req('/api/categorize', { method: 'POST', body: JSON.stringify({ transaction_id, category_slug }) });
  },
  manualEntry(entry: { occurred_at: string; amount_cents: number; merchant?: string; description?: string; category_slug: string; account_id?: string }) {
    return req<{ id: string }>('/api/manual_entry', { method: 'POST', body: JSON.stringify(entry) });
  },
  plaidLinkToken(): Promise<{ link_token: string }> {
    return req('/api/plaid/link_token', { method: 'POST', body: JSON.stringify({}) });
  },
  plaidExchange(public_token: string, display_name: string) {
    return req<{ account_id: string }>('/api/plaid/exchange', {
      method: 'POST',
      body: JSON.stringify({ public_token, display_name }),
    });
  },
};
